import { addItemToSelection } from "@/actions/selection/client/add-item-to-selection"
import { IconShoppingBag } from "@/app/(site)/_components/Atoms/Icons"
import IconNotification from "@/app/(site)/_components/Atoms/Icons/IconNotification"
import CutoverMessageModal from "@/app/(site)/_components/Atoms/CutoverMessageModal"
import { calculateButtonState } from "@/app/(site)/_components/Page/Product/ProductDetail/NewAddToCartButton/calculateButtonStates"
import useCutoverMode from "@/app/(site)/_utils/_hooks/SWR/useCutoverMode"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { useProductStore } from "@/app/(site)/contexts/Global"
import {
    type ProductData,
    type SizeInfo
} from "@/lib/content-service/types/ContentServiceProduct"
import logger from "@/lib/logger/logger-init"
import { addToCartDepictEvent } from "@/lib/utils/tracking/depictEvents"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { useMemo, useState } from "react"
import { useCookieBot } from "@/src/hooks/useCookieBot"
import { usePathname } from "next/navigation"

type AddToCartProps = {
    setErrorStyles: React.Dispatch<React.SetStateAction<boolean>>
    product: ProductData
    langCode: string
    sizes: SizeInfo[]
    firstAvailableSize?: SizeInfo
}

export interface AddToCartFormData extends FormData {
    append(name: "item", value: string | Blob, fileName?: string): void
}

export default function NewAddToCartButton({
    product,
    setErrorStyles,
    langCode,
    sizes,
    firstAvailableSize
}: AddToCartProps) {
    const availableSizesContext = useProductStore(state => state.availableSizes)
    const setAddedProduct = useProductStore(state => state.setAddedProduct)
    const { mutate } = useSWRBasketSessionState()

    // Cutover mode is read from the server at runtime (see useCutoverMode) — never from a
    // NEXT_PUBLIC env var, which is build-time inlined and cannot reflect a flag flip.
    const isCutoverActive = useCutoverMode()
    const [isCutoverModalOpen, setIsCutoverModalOpen] = useState(false)

    // Use sizes prop as fallback when Redux state is empty (e.g., when JS is disabled)
    const effectiveSizes =
        availableSizesContext.length > 0
            ? availableSizesContext
            : sizes.map(size => ({
                  ...size,
                  isSelected: firstAvailableSize?.name === size.name
              }))

    // Button state
    const { canAddToCart, isAnySizeSelected, selectedSize, item, label, action } =
        useMemo(
            () => calculateButtonState(effectiveSizes, langCode),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [JSON.stringify(effectiveSizes), langCode]
        )

    const pathname = usePathname()

    // Contexts and hooks
    const setCartMenuOpen = useEcommerceStore(state => state.toggleCartMenu)
    const toggleNotifyModal = useEcommerceStore(state => state.toggleNotifyModal)
    const { consent } = useCookieBot()
    const siteContext = useSiteContext()

    const handleAddToCart = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()

        if (!canAddToCart) return

        const formData: AddToCartFormData = new FormData() as AddToCartFormData
        formData.append("item", item)
        const locale = pathname.split("/")[1]

        try {
            setCartMenuOpen()
            const result = await addItemToSelection(formData, locale)

            if (result.success) {
                //Invalidate selection query to refetch data
                await mutate()
                //tracking events
                import("@/lib/utils/tracking/gtmEvents").then(({ addToCartEvent }) => {
                    addToCartEvent(
                        product,
                        {
                            size: selectedSize.name,
                            sku: selectedSize.sku
                        },
                        siteContext,
                        "pdp"
                    )
                })

                if (consent.necessary) {
                    addToCartDepictEvent(product, {
                        size: selectedSize.name,
                        sku: selectedSize.sku
                    })
                }
                //Update Product state
                setAddedProduct({
                    name: selectedSize.name,
                    size: selectedSize.size,
                    image: product?.media?.[0]?.url,
                    price: product.price as string,
                    color_name: product?.color_swatch?.color_name as string
                })
            } else {
                logger.error(`Failed to add product to cart from PDP`, result.error)
            }
        } catch (error) {
            logger.error(`Error adding product to cart from PDP`, error)
        }
    }

    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()

        // Show maintenance message if cutover mode is active
        if (isCutoverActive) {
            setIsCutoverModalOpen(true)
            return
        }

        switch (action) {
            case "add-to-cart":
                if (!isAnySizeSelected) {
                    // No size selected - show error message under size chart
                    setErrorStyles(true)
                } else if (canAddToCart) {
                    handleAddToCart(e)
                }
                break
            case "notify-me":
                if (!isAnySizeSelected) {
                    setErrorStyles(true)
                } else {
                    toggleNotifyModal(true)
                }
                break
            case "out-of-stock":
                break
        }
    }

    return (
        <>
            <button
                className="flex h-40 w-full items-center justify-center gap-8 rounded-full bg-black px-20 py-16 text-white font_trimMonoPoc_Regular_14_120_uppercase hover:bg-col-bg-strong lg:font_trimMonoPoc_Regular_15_130_uppercase"
                aria-label={label}
                type="submit"
                form="product-form"
                onClick={handleButtonClick}
            >
                {action === "notify-me" ? (
                    <IconNotification aria-hidden="true" className="h-16 w-16" />
                ) : (
                    <IconShoppingBag aria-hidden="true" className="h-16 w-16" />
                )}

                {label}
            </button>

            <CutoverMessageModal
                isOpen={isCutoverModalOpen}
                onClose={() => setIsCutoverModalOpen(false)}
            />
        </>
    )
}
